import * as yup from 'yup';
import { Button } from 'components/ui/button';
import { Card } from 'components/ui/card';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from 'components/ui/form';
import { Input } from 'components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/ui/select';
import { Textarea } from 'components/ui/textarea';
import { CircleCheckBig } from 'lucide-react';
import { TEST_IDS } from 'mocks/ids';
import { getFileTypeFromName } from 'pages/model/utils';
import { useForm } from 'react-hook-form';
import {
  ICreateReferenceMutation,
  IReferences,
  PlyableManufactureType,
} from 'modules/threads/types';
import { FILE_PARAMS, PLYABLE_ALLOWED_FILES, THREADS_3D_ALLOWED_FILES } from 'constants/index';
import { usePlayableStatus } from 'modules/playable';
import { Spinner } from 'ui';
import { validationSchema } from 'utils/validation';
import { yupResolver } from '@hookform/resolvers/yup';
import { FileUploadType } from 'modules/app/modals/DuplicateFileVersion';
import useFileDuplicationHandler from 'hooks/useFileDuplicationHandler';
import { CopilotDataNotice, PlyableIcon, Threads3dIcon } from '../common';
import { VersionHistory } from './version-history';

const fileInputAccept = (reference?: IReferences) => {
  if (reference?.referenceType === 'PLYABLE_FILE') return PLYABLE_ALLOWED_FILES.join(', ');
  if (reference?.referenceType === 'THREADS_3D_FILE') return THREADS_3D_ALLOWED_FILES.join(', ');
  return FILE_PARAMS.ACCEPT;
};

type FormFields = {
  name: string;
  description: string | undefined;
  file: File | undefined;
  plyable: 'disabled' | 'COMPONENT' | 'TOOL';
};

export const buildRefPayload = (
  fields: FormFields,
  isModelFile: boolean
): Omit<ICreateReferenceMutation, 'thread'> => {
  const payload: Omit<ICreateReferenceMutation, 'thread'> = {
    referenceType: isModelFile ? 'THREADS_3D_FILE' : 'FILE',
    name: fields.name,
    description: fields.description ?? null,
    url: '',
    file: fields.file,
  };

  if (fields.plyable !== 'disabled') {
    payload.plyableQuoteFor = fields.plyable;
    payload.referenceType = 'PLYABLE_FILE';
  }
  return payload;
};

const validation = yup.object({
  name: validationSchema.stringRequired(),
  description: validationSchema.textArea(),
  file: yup.mixed(),
  plyable: yup
    .mixed<PlyableManufactureType | 'disabled'>()
    .oneOf(['disabled', 'COMPONENT', 'TOOL'])
    .required(),
});

type Props = {
  reference?: IReferences;
  onSubmit: (payload: Omit<ICreateReferenceMutation, 'thread'>) => Promise<void>;
  isSubmitting: boolean;
  threadId: string;
};

// I've kept this as one component for editing and creating since the only parts needing changing
// are the file input label and accepted file types
export const FileForm = ({ reference, onSubmit, isSubmitting, threadId }: Props) => {
  const form = useForm({
    defaultValues: reference
      ? {
          name: reference.name,
          description: reference.description ?? '',
          plyable: reference.fileVersions.slice(-1)[0].plyableQuoteFor ?? 'disabled',
        }
      : {
          name: '',
          description: '',
          file: null,
          plyable: 'disabled',
        },
    resolver: yupResolver(validation),
    mode: 'onChange',
  });

  const { isPlayableFunctionalAvailable: plyableEnabled } = usePlayableStatus();
  const { validateDuplicateFiles, showDuplicateFileModal } = useFileDuplicationHandler();

  const fileType = getFileTypeFromName(form.watch('file')?.name ?? '');
  const isModelFile = fileType === 'stl' || fileType === 'step';

  const handleSubmit = async (fields: yup.InferType<typeof validation>) => {
    const payload = buildRefPayload(fields, isModelFile);
    if (!payload.file) delete payload.file;
    await onSubmit(payload);
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;

    if (!files || files.length === 0) {
      return;
    }

    const { duplicateFiles, duplicateFileReference } = await validateDuplicateFiles(
      files,
      threadId
    );

    // If we have a duplicate, make sure it's not the one we're currently editing
    // If we're creating a new one `reference` will be undefined
    if (duplicateFileReference && duplicateFileReference.id !== reference?.id) {
      showDuplicateFileModal(
        duplicateFiles,
        FileUploadType.SingleFile,
        threadId,
        duplicateFileReference
      );
    }
  };

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(handleSubmit)}
        className="space-y-4 px-1"
        data-testid={TEST_IDS.add_reference_file_tab}
      >
        {reference && <VersionHistory reference={reference} />}
        <FormField
          control={form.control}
          name="file"
          render={({ field: { value, onChange, ...field } }) => (
            <FormItem>
              <FormLabel>
                {reference ? (
                  'New File Version'
                ) : (
                  <>
                    File <span className="text-app-red">*</span>
                  </>
                )}
              </FormLabel>
              <FormControl>
                <Input
                  required={!reference}
                  type="file"
                  {...field}
                  defaultValue={value}
                  className="cursor-pointer"
                  aria-label="file-input"
                  size={FILE_PARAMS.SIZE}
                  accept={fileInputAccept(reference)}
                  onChange={(event) => {
                    handleFileUpload(event);
                    onChange(event.target.files && event.target.files[0]);
                  }}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                Name <span className="text-app-red">*</span>
              </FormLabel>
              <FormControl>
                <Input required placeholder="Name" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Description</FormLabel>
              <FormControl>
                <Textarea placeholder="Description" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        {isModelFile && (
          <div className="space-y-2" data-testid={TEST_IDS.add_reference_integrations}>
            <span className="text-sm font-medium">Integrations</span>
            <Card className="flex flex-row items-center justify-between p-3 text-sm">
              <div className="flex flex-row items-center gap-2">
                <Threads3dIcon />
                <span>Threads3D</span>
              </div>
              <div className="flex flex-row items-center gap-2">
                <span>Enabled</span>
                <CircleCheckBig className="h-4 w-4" />
              </div>
            </Card>

            {/* TODO - We shouldn't need this messiness if files can be Threads3d and Plyable */}
            {((reference &&
              reference.referenceType === 'PLYABLE_FILE' &&
              fileType === 'step' &&
              plyableEnabled) || // File is already Plyable
              (!reference && fileType === 'step' && plyableEnabled)) && ( // New File is compatible with Plyable
              <FormField
                control={form.control}
                name="plyable"
                render={({ field }) => (
                  <FormItem>
                    <Card className="flex flex-row items-center justify-between p-3 text-sm">
                      <div className="flex flex-row items-center gap-2">
                        <PlyableIcon />
                        <FormLabel>Plyable</FormLabel>
                      </div>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger className="w-[140px]">
                            <SelectValue />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent className="z-[100]">
                          <SelectItem value="disabled">Disabled</SelectItem>
                          <SelectItem value="COMPONENT">Component</SelectItem>
                          <SelectItem value="TOOL">Tool</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </Card>
                  </FormItem>
                )}
              />
            )}
          </div>
        )}
        {!reference && <CopilotDataNotice />}
        <div className="flex w-full flex-row justify-end">
          <Button type="submit" disabled={isSubmitting}>
            Save
            {isSubmitting && (
              <div className="pl-2">
                <Spinner color="white" size="small" />
              </div>
            )}
          </Button>
        </div>
      </form>
    </Form>
  );
};
